<template>
  <div>
    <!-- ==================================================================
         INTERNAL CHROME — not part of what a practice sees. Kept on a gray
         band with an explicit label so the boundary is unmistakable during a
         demo: everything below the divider is the practice's view.
         ================================================================== -->
    <div class="bg-growth-10 border-b border-gray-200 px-5 py-4">
      <div class="flex items-end gap-4 flex-wrap">
        <div class="grow max-w-md">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Internal · beta preview
          </label>
          <select
            v-model="selectedPractice"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
            @change="loadAddresses"
          >
            <option :value="null" disabled>Select a practice to preview…</option>
            <option v-for="p in practices" :key="p.practice_entity" :value="p.practice_entity">
              {{ p.practice_name }} — {{ p.addresses }} address{{ p.addresses === 1 ? '' : 'es' }}<span v-if="p.tins > 1"> · {{ p.tins }} TINs</span><span v-if="p.pending"> · {{ p.pending }} pending</span>
            </option>
          </select>
        </div>
        <p class="text-xs text-gray-500 pb-2 max-w-sm">
          The practice never sees this selector. They arrive on a link scoped to
          their own practice only.
        </p>
      </div>
    </div>

    <div v-if="error" class="mx-5 mt-4 p-4 bg-red-100 border border-red-300 rounded-lg text-sm text-red-800">
      {{ error }}
    </div>

    <div v-if="!selectedPractice" class="px-5 py-10 text-center text-sm text-gray-500">
      Select a practice above to see what they would be asked to confirm.
    </div>
    <LoadingIndicator v-else-if="loading" message="Loading addresses…" />

    <!-- ==================================================================
         THE PRACTICE'S VIEW starts here.
         ================================================================== -->
    <template v-else>
      <PageHeader
        :title="practiceName"
        :heading-level="2"
        subtitle="Confirm your practice locations for Medicare ACO reporting"
      />

      <div class="p-5">
        <div class="max-w-3xl">
          <p class="text-sm text-gray-800">
            Please confirm the locations below are places your practice bills from.
            Correct anything that is wrong, reject what is not yours, and add any we
            have missed. The county for each location determines the service area we
            report, so it matters that these are right.
          </p>

          <!-- Attestation: who is answering. No login, so this is the only -->
          <!-- record of who confirmed, which is why it sits with the content. -->
          <div class="mt-4 flex items-end gap-4 flex-wrap">
            <div class="max-w-xs grow">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Your name <span class="text-gray-500 font-normal">— so we know who confirmed</span>
              </label>
              <input
                v-model="reviewerName"
                type="text"
                placeholder="First and last name"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
              />
            </div>
            <div class="flex items-center gap-2 pb-2 text-xs">
              <span class="text-gray-600">{{ addresses.length }} location{{ addresses.length === 1 ? '' : 's' }}</span>
              <Tag v-if="counts.pending" :label="`${counts.pending} to review`" color="amber" />
              <Tag v-if="counts.approved" :label="`${counts.approved} confirmed`" color="emerald" />
              <Tag v-if="counts.rejected" :label="`${counts.rejected} rejected`" color="red" />
            </div>
          </div>

          <!-- Address cards -->
          <div class="mt-5 space-y-4">
            <div
              v-for="row in addresses"
              :key="row.id"
              class="bg-white rounded-lg border overflow-hidden"
              :class="row.status === 'rejected' ? 'border-gray-200' : 'border-growth-50'"
            >
              <!-- ---------- read-only ---------- -->
              <div v-if="editingId !== row.id" class="p-4">
                <div class="flex items-start justify-between gap-4 flex-wrap">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2 mb-2 flex-wrap">
                      <Tag :label="statusLabel(row)" :color="statusColor(row)" />
                      <Tag v-if="row.edited" label="Corrected" color="blue" />
                      <Tag v-if="row.source === 'practice_added'" label="Added by you" color="blue" />
                    </div>

                    <p
                      class="text-sm font-medium text-gray-900"
                      :class="row.status === 'rejected' ? 'line-through text-gray-500' : ''"
                    >
                      {{ row.address_line1 || 'No address on file — please add yours' }}<span
                        v-if="row.address_line2"
                        class="font-normal text-gray-800"
                      >, {{ row.address_line2 }}</span>
                    </p>
                    <p class="text-sm text-gray-600">
                      {{ row.city }}<span v-if="row.city && row.state">,</span> {{ row.state }} {{ row.zip_code }}
                    </p>
                    <p class="text-xs text-gray-600 mt-2">
                      County <span class="font-medium text-gray-800">{{ row.county || 'not determined' }}</span>
                      <span v-if="row.is_primary_care === true" class="ml-4">Primary care delivered here</span>
                      <span v-else-if="row.is_primary_care === false" class="ml-4">No primary care here</span>
                    </p>

                    <p v-if="row.practice_comment" class="mt-2 text-xs text-gray-600">
                      “{{ row.practice_comment }}”<span v-if="row.practice_reviewer_name" class="text-gray-500"> — {{ row.practice_reviewer_name }}</span>
                    </p>
                  </div>

                  <div class="flex items-center gap-2 shrink-0">
                    <button
                      class="btn-primary-sm"
                      :disabled="busyId === row.id"
                      @click="decide(row, 'approved')"
                    >
                      Confirm
                    </button>
                    <button
                      class="btn-secondary-sm"
                      :disabled="busyId === row.id"
                      @click="startReject(row)"
                    >
                      Reject
                    </button>
                    <button class="btn-secondary-sm" @click="startEdit(row)">Edit</button>
                  </div>
                </div>

                <!-- reject reason -->
                <div v-if="rejectingId === row.id" class="mt-4 pt-4 border-t border-gray-200">
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Why is this not one of your locations?
                    <span class="text-gray-500 font-normal">— optional</span>
                  </label>
                  <div class="flex items-center gap-2 flex-wrap">
                    <input
                      v-model="rejectComment"
                      type="text"
                      placeholder="e.g. we moved out of this suite in 2024"
                      class="grow max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
                      @keyup.enter="decide(row, 'rejected')"
                    />
                    <button class="btn-danger-sm" @click="decide(row, 'rejected')">
                      Reject location
                    </button>
                    <button class="btn-link text-xs" @click="rejectingId = null">Cancel</button>
                  </div>
                </div>
              </div>

              <!-- ---------- edit / add ---------- -->
              <div v-else class="p-4 bg-growth-10">
                <div class="grid grid-cols-6 gap-4">
                  <div class="col-span-6 sm:col-span-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Street address</label>
                    <input v-model="draft.address_line1" type="text" :class="inputClass" />
                  </div>
                  <div class="col-span-6 sm:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Suite / unit</label>
                    <input v-model="draft.address_line2" type="text" :class="inputClass" />
                  </div>
                  <div class="col-span-6 sm:col-span-3">
                    <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input v-model="draft.city" type="text" :class="inputClass" />
                  </div>
                  <div class="col-span-2 sm:col-span-1">
                    <label class="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input v-model="draft.state" type="text" maxlength="2" :class="inputClass" />
                  </div>
                  <div class="col-span-4 sm:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                    <input v-model="draft.zip_code" type="text" maxlength="10" :class="inputClass" />
                  </div>
                  <div class="col-span-6 sm:col-span-3">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      County <span class="text-gray-500 font-normal">— sets your service area</span>
                    </label>
                    <input v-model="draft.county" type="text" :class="inputClass" />
                  </div>
                  <div class="col-span-6 sm:col-span-3 flex items-end">
                    <label class="flex items-center gap-2 text-sm text-gray-700 pb-2">
                      <input
                        v-model="draft.is_primary_care"
                        type="checkbox"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                      />
                      Primary care is delivered here
                    </label>
                  </div>
                </div>

                <div class="flex items-center gap-2 mt-5 flex-wrap">
                  <button class="btn-primary-sm" :disabled="busyId === row.id" @click="saveEdit(row)">
                    {{ busyId === row.id ? 'Saving…' : (isNew(row) ? 'Add location' : 'Save correction') }}
                  </button>
                  <button class="btn-link text-xs" @click="cancelEdit">Cancel</button>
                  <span v-if="!isNew(row)" class="text-xs text-gray-500 ml-2">
                    We keep a record of the address we originally had.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <button
            v-if="!addingNew"
            class="w-full mt-4 py-4 text-sm text-gray-600 bg-white border border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:text-gray-800 transition-colors"
            @click="startAdd"
          >
            + Add a location we missed
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'

const BASE = '/api/rostering/addresses/'

const STATUS_LABELS = {
  pending: 'Needs review',
  approved: 'Confirmed',
  rejected: 'Rejected',
  superseded: 'Replaced',
}
const STATUS_COLORS = {
  pending: 'amber',
  approved: 'emerald',
  rejected: 'red',
  superseded: 'gray',
}

export default {
  name: 'PracticeRosterView',
  components: { PageHeader, LoadingIndicator, Tag },
  props: {
    // Set when arriving from a report row, so the picker lands pre-selected.
    initialPractice: { type: [Number, String], default: null },
  },
  data() {
    return {
      practices: [],
      selectedPractice: null,
      reviewerName: '',
      addresses: [],
      loading: false,
      error: null,
      editingId: null,
      rejectingId: null,
      rejectComment: '',
      busyId: null,
      addingNew: false,
      draft: {},
      inputClass:
        'w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent',
    }
  },
  computed: {
    practiceName() {
      const p = this.practices.find(x => x.practice_entity === this.selectedPractice)
      return p ? p.practice_name : ''
    },
    counts() {
      const tally = { pending: 0, approved: 0, rejected: 0 }
      for (const r of this.addresses) {
        if (tally[r.status] !== undefined) tally[r.status]++
      }
      return tally
    },
  },
  watch: {
    initialPractice(id) {
      if (id) this.selectPractice(id)
    },
  },
  mounted() {
    this.loadPractices()
  },
  methods: {
    async loadPractices() {
      try {
        const { data } = await axios.get(`${BASE}practices/`)
        this.practices = data
        if (this.initialPractice) this.selectPractice(this.initialPractice)
      } catch (e) {
        this.error = this.errText(e, 'Could not load practices')
      }
    },
    selectPractice(id) {
      this.selectedPractice = Number(id)
      this.loadAddresses()
    },
    async loadAddresses() {
      if (!this.selectedPractice) return
      this.loading = true
      this.error = null
      this.resetForms()
      try {
        const { data } = await axios.get(BASE, {
          params: { practice_entity: this.selectedPractice },
        })
        this.addresses = data.results || data
      } catch (e) {
        this.error = this.errText(e, 'Could not load addresses')
      } finally {
        this.loading = false
      }
    },

    // ---- review -----------------------------------------------------
    async decide(row, decision) {
      this.busyId = row.id
      this.error = null
      try {
        const { data } = await axios.post(`${BASE}${row.id}/decide/`, {
          decision,
          side: 'practice',
          reviewer_name: this.reviewerName,
          comment: decision === 'rejected' ? this.rejectComment : '',
        })
        this.replaceRow(row.id, data)
        this.rejectingId = null
        this.rejectComment = ''
      } catch (e) {
        this.error = this.errText(e, 'Could not save that')
      } finally {
        this.busyId = null
      }
    },
    startReject(row) {
      this.rejectingId = row.id
      this.rejectComment = ''
      this.editingId = null
    },

    // ---- edit (supersede) / add -------------------------------------
    startEdit(row) {
      this.editingId = row.id
      this.rejectingId = null
      this.draft = {
        address_line1: row.address_line1,
        address_line2: row.address_line2,
        city: row.city,
        state: row.state,
        zip_code: row.zip_code,
        county: row.county,
        is_primary_care: row.is_primary_care === true,
      }
    },
    startAdd() {
      // A new location still needs a TIN, which this page deliberately never
      // shows. Anchor it to a TIN the practice already has; internal staff can
      // reassign it later.
      const anchor = this.addresses[0]
      if (!anchor) {
        this.error = 'Nothing to anchor a new address to yet for this practice.'
        return
      }
      const placeholder = {
        id: '__new__',
        tin_entity: anchor.tin_entity,
        practice_entity: this.selectedPractice,
        status: 'pending',
        source: 'practice_added',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        county: '',
        is_primary_care: null,
        edited: false,
      }
      this.addresses.push(placeholder)
      this.addingNew = true
      this.startEdit(placeholder)
    },
    isNew(row) {
      return row.id === '__new__'
    },

    async saveEdit(row) {
      this.busyId = row.id
      this.error = null
      try {
        if (this.isNew(row)) {
          const { data } = await axios.post(BASE, {
            tin_entity: row.tin_entity,
            practice_entity: this.selectedPractice,
            side: 'practice',
            reviewer_name: this.reviewerName,
            ...this.draft,
          })
          // Replace by identity, not by position — the placeholder is appended
          // today but relying on "it is last" breaks the moment anything else
          // reorders the list.
          const n = this.addresses.findIndex(r => this.isNew(r))
          if (n !== -1) this.addresses.splice(n, 1, data)
        } else {
          // Supersede rather than mutate — the address we sent is preserved.
          const { data } = await axios.post(`${BASE}${row.id}/supersede/`, {
            side: 'practice',
            reviewer_name: this.reviewerName,
            ...this.draft,
          })
          this.replaceRow(row.id, data)
        }
        this.cancelEdit()
      } catch (e) {
        this.error = this.errText(e, 'Could not save the correction')
      } finally {
        this.busyId = null
      }
    },
    cancelEdit() {
      if (this.addingNew) {
        const i = this.addresses.findIndex(r => this.isNew(r))
        if (i !== -1) this.addresses.splice(i, 1)
      }
      this.editingId = null
      this.addingNew = false
      this.draft = {}
    },

    // ---- helpers ----------------------------------------------------
    replaceRow(id, data) {
      const i = this.addresses.findIndex(r => r.id === id)
      if (i !== -1) this.addresses.splice(i, 1, data)
    },
    resetForms() {
      this.editingId = null
      this.rejectingId = null
      this.addingNew = false
      this.draft = {}
    },
    statusLabel(row) {
      return STATUS_LABELS[row.status] || row.status
    },
    statusColor(row) {
      return STATUS_COLORS[row.status] || 'gray'
    },
    errText(e, fallback) {
      return e.response?.data?.detail || e.response?.data?.error || fallback
    },
  },
}
</script>
